import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Minus, Plus, Truck, Check, Star, ShieldCheck, Sparkles, Droplets, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../services/api";
import { formatPrice, discountPercent } from "../utils/formatPrice";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useFavorites } from "../context/FavoritesContext";
import ProductCard from "../components/product/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Skeleton } from "../components/ui/skeleton";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, openCart } = useCart();
  const { user } = useAuth();
  const fav = useFavorites();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setActiveImg(0);
        setQty(1);
      })
      .catch(() => navigate("/shop"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading || !product) {
    return (
      <div className="container-queen pt-32 pb-24 grid lg:grid-cols-2 gap-12" id="main">
        <Skeleton className="w-full rounded-3xl aspect-[4/5]" />
        <div className="space-y-4">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-6 w-1/4" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const discount = discountPercent(product.compare_price, product.price);
  const isFav = fav?.isFavorite(product.id);
  const isVitC = product.slug?.includes("vitamine-c");
  const isHyal = product.slug?.includes("acide-hyaluronique");

  const handleAdd = () => {
    addToCart(product, null, qty);
    toast.success(`${product.name} ajouté à votre rituel`);
    openCart();
  };

  const handleFav = async () => {
    if (!user) {
      toast("Connectez-vous pour enregistrer vos favoris");
      return;
    }
    const added = await fav.toggleFavorite(product.id);
    toast.success(added ? "Ajouté à votre sélection" : "Retiré de votre sélection");
  };

  return (
    <div className="container-queen pt-32 pb-24" id="main">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-[#888888] mb-10 font-body uppercase tracking-[0.14em]">
        <Link to="/" className="hover:text-[#111111] transition-colors">Accueil</Link>
        <span>/</span>
        <Link to="/shop" className="hover:text-[#111111] transition-colors">Sérums</Link>
        <span>/</span>
        <span className="text-[#111111] font-medium">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left Column: Gallery */}
        <div className="lg:col-span-6 space-y-4">
          <div className="relative rounded-3xl overflow-hidden bg-[#F8F8F6] border border-[#EAE8E4] aspect-[4/5] p-8 flex items-center justify-center">
            <img
              src={mediaUrl(product.images[activeImg])}
              alt={product.name}
              className="w-full h-full object-contain mix-blend-multiply transition-transform duration-700 hover:scale-105"
              data-testid="product-main-image"
            />
            {discount && (
              <span className="absolute top-5 left-5 bg-[#111111] text-white text-[11px] font-body tracking-[0.16em] uppercase px-3 py-1 rounded-full">
                -{discount}
              </span>
            )}
            <button
              onClick={handleFav}
              aria-label={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
              className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-[#EAE8E4] grid place-items-center text-[#111111] hover:bg-white transition-colors shadow-sm"
            >
              <Heart size={18} fill={isFav ? "#111111" : "none"} />
            </button>
          </div>

          {/* Thumbnail Strip */}
          {product.images?.length > 1 && (
            <div className="flex gap-3">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  className={`w-20 h-24 rounded-xl overflow-hidden bg-[#F8F8F6] border-2 transition-all p-1.5 ${
                    idx === activeImg ? "border-[#111111]" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={mediaUrl(img)} alt="" className="w-full h-full object-contain mix-blend-multiply" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Column: Product Intelligence */}
        <div className="lg:col-span-6 space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-[11px] font-body uppercase tracking-[0.18em] text-[#C5A880] font-medium">
                {product.category_name || "Sérum de Soin"}
              </span>
              <span className="text-[11px] font-body uppercase tracking-[0.14em] text-[#888888] border-l border-[#EAE8E4] pl-3">
                Contenance {product.volume || "30mL"}
              </span>
            </div>

            <h1 className="font-display text-3xl sm:text-4xl text-[#111111] leading-tight">
              {product.name}
            </h1>

            {/* Stars & Clinical Proof */}
            <div className="flex items-center gap-3 mt-3">
              <div className="flex text-[#C5A880]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={15} fill="#C5A880" />
                ))}
              </div>
              <span className="text-xs text-[#666666] font-body">
                4.9/5 · Recommandé par 98% des utilisatrices
              </span>
            </div>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-3 py-4 border-y border-[#EAE8E4]">
            <span className="font-body text-2xl font-medium text-[#111111]">
              {formatPrice(product.price)}
            </span>
            {product.compare_price && (
              <span className="font-body text-base text-[#888888] line-through">
                {formatPrice(product.compare_price)}
              </span>
            )}
            <span className="text-xs text-[#059669] font-medium ml-2">
              Paiement à la livraison
            </span>
          </div>

          {/* Description */}
          <p className="text-sm text-[#555555] font-body leading-relaxed">
            {product.description}
          </p>

          {/* Quantity and Add to Bag */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center border border-[#EAE8E4] rounded-full h-12 px-2 bg-white">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-8 h-8 grid place-items-center text-[#666666] hover:text-[#111111]"
                  aria-label="Diminuer la quantité"
                >
                  <Minus size={15} />
                </button>
                <span className="w-10 text-center font-body text-sm font-medium">{qty}</span>
                <button
                  onClick={() => setQty((q) => Math.min(product.stock || 50, q + 1))}
                  className="w-8 h-8 grid place-items-center text-[#666666] hover:text-[#111111]"
                  aria-label="Augmenter la quantité"
                >
                  <Plus size={15} />
                </button>
              </div>

              <button
                onClick={handleAdd}
                disabled={product.stock === 0}
                className="flex-1 btn-queen-primary h-12 text-sm uppercase tracking-wider"
              >
                <ShoppingBag size={17} />
                {product.stock === 0 ? "Rupture de stock" : "Commander maintenant"}
              </button>
            </div>

            {/* Delivery Guarantee Alert */}
            <div className="bg-[#F8F8F6] border border-[#EAE8E4] rounded-2xl p-4 flex items-center justify-between text-xs text-[#555555]">
              <span className="flex items-center gap-2">
                <Truck size={16} className="text-[#C5A880]" />
                Livraison express dans les 58 Wilayas
              </span>
              <span className="font-medium text-[#111111]">
                Règlement à la réception
              </span>
            </div>
          </div>

          {/* Technical Tabs / Accordion */}
          <div className="pt-4">
            <Accordion type="single" collapsible defaultValue="protocol">
              <AccordionItem value="protocol" className="border-b border-[#EAE8E4]">
                <AccordionTrigger className="text-sm font-body font-medium text-[#111111] hover:no-underline py-4">
                  Rituel d'Application
                </AccordionTrigger>
                <AccordionContent className="text-xs text-[#666666] leading-relaxed space-y-2 pb-4">
                  <p>
                    {isVitC
                      ? "Matin : Appliquer 3 à 4 gouttes sur une peau propre et sèche avant votre crème de jour et votre protection solaire. Éviter le contact direct avec les yeux."
                      : isHyal
                      ? "Soir : Déposer 3 gouttes sur une peau propre et légèrement humidifiée. Faire pénétrer par pressions délicates sur le visage, le cou et le décolleté."
                      : "Matin & Soir : Alterner le Sérum Vitamine C le matin et le Sérum Acide Hyaluronique le soir pour un protocole cutané complet de régénération 24h."}
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="ingredients" className="border-b border-[#EAE8E4]">
                <AccordionTrigger className="text-sm font-body font-medium text-[#111111] hover:no-underline py-4">
                  Composition &amp; Actifs Clés
                </AccordionTrigger>
                <AccordionContent className="text-xs text-[#666666] leading-relaxed pb-4">
                  <p>
                    Formulation haute pureté : Aqua, Sodium Ascorbyl Phosphate / Sodium Hyaluronate, Tocopheryl Acetate (Vitamine E), Glycerin, Phenoxyethanol, Ethylhexylglycerin, Rosa Damascena Flower Water.
                  </p>
                  <p className="mt-2 text-[#111111] font-medium">
                    100% sans parabènes, sans silicones et sans huiles minérales occlusives.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="skin-types" className="border-b border-[#EAE8E4]">
                <AccordionTrigger className="text-sm font-body font-medium text-[#111111] hover:no-underline py-4">
                  Types de Peaux Recommandés
                </AccordionTrigger>
                <AccordionContent className="text-xs text-[#666666] leading-relaxed pb-4">
                  Convient à toutes les typologies cutanées : peaux normales, sèches, mixtes, grasses et sensibles. Testé sous contrôle dermatologique strict.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {/* Related Products */}
      {product.related?.length > 0 && (
        <div className="mt-28 pt-16 border-t border-[#EAE8E4]">
          <h3 className="font-display text-2xl sm:text-3xl text-[#111111] mb-8">
            Complétez votre Rituel
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {product.related.map((rel) => (
              <ProductCard key={rel.id} product={rel} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
